import { isDevMode } from '@angular/core';
import { FormControlStatus } from '@angular/forms';
import { GroupedFormData } from '@navstation-portal/reporting/data-model/ui';
import {
  IReportTabState,
  InitMode,
  ReportTabRulesKey,
  StateFieldType,
  StoreFormState,
  initialFormState,
} from '@navstation-portal/reporting/data-model/vessel-report';
import { authEvents } from '@navstation-portal/shared/data-access/auth';
import { VesselSpec } from '@navstation-portal/vessels/data-model/vessel';
import { signalStoreFeature, type } from '@ngrx/signals';
import { on, withReducer } from '@ngrx/signals/events';
import { CalculatedValueWrite, FormStoreEventGroup, formatCalculatedValuePath } from './create-form-store-events';
import { stampInitialStatuses } from './form-validity.utility';
import { getStateFieldFromFormState, isStateField } from './vm-builder-helpers';

const updateProperty = (
  state: StoreFormState,
  sectionId: string,
  fieldId: string,
  property: string,
  value: StateFieldType,
  index: number | undefined,
  nestedFieldId?: string | undefined
) => {
  const entry = state.formState[sectionId][fieldId];
  if (isStateField(entry)) {
    if (value === entry[property]) return {};

    return updateStateFieldProperty(state, sectionId, fieldId, property, value as boolean);
  }

  if (index != null && property != null && nestedFieldId != null) {
    const nestedEntry = entry[index][nestedFieldId];
    if (value === nestedEntry[property]) return {};

    return updateNestedProperty(state, sectionId, fieldId, property, value as boolean, index, nestedFieldId);
  }

  return {};
};

const updateStateFieldProperty = (
  state: StoreFormState,
  sectionId: string,
  fieldId: string,
  property: string,
  value: StateFieldType
) => {
  if (!state.formState) return {};

  const section = state.formState[sectionId];
  if (!section) return {};

  const field = section[fieldId];
  if (!field) return {};

  return {
    ...state,
    formState: {
      ...state.formState,
      [sectionId]: {
        ...section,
        [fieldId]: {
          ...field,
          [property]: value,
        },
      },
    },
  };
};

const updateNestedProperty = (
  state: StoreFormState,
  sectionId: string,
  fieldId: string,
  property: string,
  value: StateFieldType,
  index: number | undefined,
  nestedFieldId: string | undefined
) => {
  if (!state.formState || index == null || !nestedFieldId) return {};

  const section = state.formState[sectionId];
  if (!section) return state;

  const fieldArray = section[fieldId];
  if (!Array.isArray(fieldArray)) return state;

  const field = fieldArray[index];
  if (!field) return state;

  const nestedField = field[nestedFieldId];
  if (!nestedField) return state;

  const updatedNestedField = {
    ...nestedField,
    [property]: value,
  };

  return {
    ...state,
    formState: {
      ...state.formState,
      [sectionId]: {
        ...section,
        [fieldId]: [
          ...fieldArray.slice(0, index),
          {
            ...field,
            [nestedFieldId]: updatedNestedField,
          },
          ...fieldArray.slice(index + 1),
        ],
      },
    },
  };
};

const writeCalculatedValue = (state: StoreFormState, write: CalculatedValueWrite): StoreFormState => {
  const { sectionId, fieldId, value, index, property } = write;
  // updateProperty drops a write to a path that it does not find, with no
  // message. withCalculatedValues sends no such write, so this warning catches
  // a write from a different sender.
  if (!getStateFieldFromFormState(state.formState ?? {}, sectionId, fieldId, index, property)) {
    if (isDevMode()) {
      console.warn(`calculatedValuesChanged: the store holds no field at ${formatCalculatedValuePath(write)}`);
    }
    return state;
  }

  return { ...state, ...updateProperty(state, sectionId, fieldId, 'value', value, index, property) };
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function withFormStoreReducer<_>(
  events: FormStoreEventGroup,
  initReducer?: (
    initialData: IReportTabState,
    initMode: InitMode,
    vesselSpec: VesselSpec | undefined,
    formFieldRules?: Record<string, GroupedFormData>
  ) => Partial<NoInfer<StoreFormState>>,
  // When provided, field statuses are stamped from the rules at init so the tab
  // reports accurate validity without being rendered.
  tabId?: ReportTabRulesKey
) {
  return signalStoreFeature(
    type<{ state: StoreFormState }>(),
    withReducer(
      on(events.initialiseFormState, ({ payload: { initialData, initMode, vesselSpec, formFieldRules } }) => () => {
        if (!initialData || !initReducer) return {};

        const built = initReducer(initialData, initMode, vesselSpec, formFieldRules);

        if (!tabId || !formFieldRules || !built.formState) return built;

        return { ...built, formState: stampInitialStatuses(built.formState, formFieldRules[tabId]) };
      }),
      on(events.updateFormStateTouched, ({ payload: { sectionId, fieldId, value, index, property } }) => (state) => {
        return updateProperty(state, sectionId, fieldId, 'touched', value as boolean, index, property);
      }),
      on(events.updateFormStatePristine, ({ payload: { sectionId, fieldId, value, index, property } }) => (state) => {
        return updateProperty(state, sectionId, fieldId, 'pristine', value as boolean, index, property);
      }),
      on(events.updateFormStateStatus, ({ payload: { sectionId, fieldId, value, index, property } }) => (state) => {
        return updateProperty(state, sectionId, fieldId, 'status', value as FormControlStatus, index, property);
      }),
      on(events.updateFormStateValue, ({ payload: { sectionId, fieldId, value, index, property } }) => (state) => {
        return updateProperty(state, sectionId, fieldId, 'value', value as StateFieldType, index, property);
      }),
      // This case sets the values and does not change a status. ReportStore
      // sets the status of a Calculated Value from the template.
      on(events.calculatedValuesChanged, ({ payload: { writes } }) => (state) => {
        return writes.reduce(writeCalculatedValue, state);
      }),
      on(authEvents.logoutSuccess, () => initialFormState)
    )
  );
}
